import React, { useState, useEffect } from "react";
import {
  Layout,
  Button,
  Table,
  Space,
  Modal,
  Input,
  Form,
  Switch,
  Popconfirm,
  message,
  Card,
  Typography,
  Upload,
  Image,
} from "antd";

import {
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DragOutlined,
} from "@ant-design/icons";

import {
  createCustomer,
  deleteCustomer,
  getCustomers,
  updateCustomer,
  updateCustomerImage,
  updateCustomerPriority,
  updateCustomerPublish,
} from "../services/customersService";

import ImageGridSorter from "./ImageGridSorter";

const { Content } = Layout;
const { Title } = Typography;

function HappyClient() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [imageBase64, setImageBase64] = useState("");

  const [form] = Form.useForm();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 5;

  const [isOrderView, setIsOrderView] = useState(false);
  const [fileList, setFileList] = useState([]);
      const [previewImage, setPreviewImage] = useState(null);
      const [previewOpen, setPreviewOpen] = useState(false);

  // Fetch

  const fetchCustomers = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getCustomers(page, pageSize);
      setData(res.data.data);
      setTotalItems(res.data.pagination.totalItems);
      setCurrentPage(page);
    } catch {
      message.error("Fetch Failed");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCustomers(currentPage);
  }, [currentPage]);

  // Create

  const handleAddClick = () => {
    setEditingItem(null);
    form.resetFields();
    setImageBase64("");

    setFileList([]);

    setOpen(true);
  };

  // Submit

  const handleSubmit = async (values) => {
    try {
      if (editingItem) {
        await updateCustomer(editingItem.id, {
          title: values.title,
          subTitle: values.subTitle,
        });
        message.success("Updated");
      } else {
        await createCustomer({
          ...values,
          image: imageBase64,
        });
        message.success("Added");
      }

      fetchCustomers(currentPage);
      setOpen(false);
      form.resetFields();
      setImageBase64("");
    } catch {
      message.error("Image required");
    }
  };

  // Delete

  const handleDelete = async (id) => {
    try {
      await deleteCustomer(id);
      message.success("Deleted");
      fetchCustomers(currentPage);
    } catch {
      message.error("Delete Failed");
    }
  };

  // Image Update

  const handleImageChange = (file, id) => {
    const reader = new FileReader();

    reader.onload = async () => {
      await updateCustomerImage(id, { image: reader.result });
      message.success("Image Updated");
      fetchCustomers(currentPage);
    };

    reader.readAsDataURL(file);
  };

  // Publish

  const togglePublish = async (id, checked) => {
    await updateCustomerPublish(id, { isPublish: checked });

    message.success(
      checked ? "Published Successfully" : "Unpublished Successfully",
    );

    fetchCustomers(currentPage);
  };

  // Save

  const handleSaveOrder = async () => {
    const reorderedItems = data.map((item, index) => ({
      id: item.id,
      priority: index,
    }));

    await updateCustomerPriority({ reorderedItems });

    message.success("Order Updated");
  };

  // Table

  const columns = [
    {
      title: "Image",
      render: (_, record) => (
        <div style={{ textAlign: "center" }}>
          <Image 
          src={record.image} 
          width={60} 
          preview={false}
          style={{ objectFit: "cover", borderRadius: 6 ,cursor: "pointer"}}
           onClick={() => {
              setPreviewImage(record.image);
              setPreviewOpen(true);
            }}
           /> 
          <br />
          <br />
          <Upload
            showUploadList={false}
            beforeUpload={(file) => {
              handleImageChange(file, record.id);
              return false;
            }}
          >
            <Button size="small" icon={<UploadOutlined />}>
              Update
            </Button>
          </Upload>
        </div>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
    },
    {
      title: "Sub Title",
      dataIndex: "subTitle",
    },
    {
      title: "Publish",
      render: (_, record) => (
        <Switch
          checked={record.isPublish}
          onChange={(checked) => togglePublish(record.id, checked)}
        />
      ),
    },
    {
      title: "Action",
      render: (_, record) => (
        <Space direction="vertical">
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingItem(record);
              setOpen(true);

              form.setFieldsValue({
                title: record.title,
                subTitle: record.subTitle,
              });
            }}
          >
            Edit
          </Button>

          <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <Content style={{ padding: 20 }}>
        <Card title={<Title level={4}>Happy Client</Title>}>
          <Button type="primary" onClick={handleAddClick}>
            Add Client
          </Button>

          <Button
            style={{ marginLeft: 10 }}
            icon={<DragOutlined />}
            onClick={async () => {
              if (!isOrderView) {
                const res = await getCustomers(1, 1000);
                setData(res.data.data);
              } else {
                fetchCustomers(currentPage);
              }

              setIsOrderView(!isOrderView);
            }}
          >
            {isOrderView ? "Back to List" : "Reorder"}
          </Button>

          {isOrderView && (
            <Button
              type="primary"
              style={{ marginLeft: 10 }}
              onClick={handleSaveOrder}
            >
              Save Order
            </Button>
          )}

          <br />
          <br />

          {isOrderView ? (
            <ImageGridSorter
              data={data.map((i) => ({
                id: i.id,
                image: i.image,
                title: i.title,
                subtitle: i.subTitle,
              }))}
              onChange={(newOrder) => {
                const updated = newOrder.map((item) =>
                  data.find((d) => d.id === item.id),
                );
                setData(updated);
              }}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={data}
              rowKey="id"
              loading={loading}
              pagination={{
                current: currentPage,
                total: totalItems,
                pageSize: pageSize,
                onChange: (page) => fetchCustomers(page),
              }}
            />
          )}
        </Card>
      </Content>

      {/* Model */}

      <Modal
        title={editingItem ? "Edit Client" : "Add Client"}
        open={open}
        onCancel={() => {
          setOpen(false);

          setFileList([]);
          setImageBase64("");
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="title" label="Title" rules={[{ required: false }]}>
            <Input />
          </Form.Item>

          <Form.Item
            name="subTitle"
            label="Sub Title"
            rules={[{ required: false }]}
          >
            <Input />
          </Form.Item>

          {!editingItem && (
            <Form.Item label="Image"  rules={[{ required: true }]}>
              <Upload
                fileList={fileList}
                maxCount={1}
                beforeUpload={(file) => {
                  const reader = new FileReader();

                  reader.readAsDataURL(file);

                  reader.onload = () => {
                    setImageBase64(reader.result);
                  };

                  setFileList([file]);
                  return false;
                }}
                onRemove={() => {
                  setFileList([]);
                  setImageBase64("");
                }}
              >
                <Button icon={<UploadOutlined />}>Upload Image</Button>
              </Upload>
            </Form.Item>
          )}

          <Button type="primary" htmlType="submit" block>
            {editingItem ? "Update" : "Add"}
          </Button>
        </Form>
      </Modal>
       {/* image preview */}
            
                    <Modal
                      open={previewOpen}
                      footer={null}
                      onCancel={() => setPreviewOpen(false)}
                    >
                      <Image src={previewImage} />
                    </Modal>
    </Layout>
  );
}

export default HappyClient;
